import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map, tap } from 'rxjs';
import { ScheduledServicesModel } from '../../models/schedule/scheduledServicesModel';
import dayjs from 'dayjs';
import { CancelScheduleRequestModel } from '../../models/schedule/cancel.schedule.request.model';
import { QuotationScheduleDetailVO } from '../../models/quotation/quotation-schedule-detail.model';
import { ScheduleServices } from '../../models/schedule/schedule-services.model';
import { AuthService, SessionService } from '@agenda-customer-ui/data-access';
import { SourceName, StorageKeys } from '@core/enums';
import { IOpenSchedule, IResponseModel, IUserProfileResponse } from '@core/interface';
import { isMobileScreen } from '@core/utils';
import { API, environment } from '@core/envs';
import { DriverInformationModel } from '../../models/schedule/driver-information.model';

@Injectable()
export class ScheduleService {
  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private sessionService: SessionService
  ) { }

  goToSchedule(schedule = {} as IOpenSchedule, reschedule = false) {
    let scheduleUrl = environment.project.schedule;    

    const hasPaymentMethod = schedule.paymentMethod?.[0]?.online ?? false;
    const rescheduleParams = reschedule ? `&scheduleId=${schedule.scheduleId}&reschedule=true&advisorId=${schedule.advisorId}&scheduleDate=${schedule.scheduleDate}&scheduleHour=${schedule.scheduleHour}&hasPaymentMethod=${hasPaymentMethod}` : '';
    const source = this.authService.urlInfo.source || isMobileScreen() ? SourceName.mobile : SourceName.web;
    scheduleUrl = scheduleUrl.concat(
      '?cat=' + this.sessionService.getItem(StorageKeys.CAT in sessionStorage ? StorageKeys.CAT : StorageKeys.AUTH_TOKEN),
      '&vin=' + this.authService.urlInfo.vin,
      '&country=' + this.authService.urlInfo.country,
      '&divisionId=1',
      '&source=' + source,
      '&dealerId=' + `${schedule.dealerId ?? ''}`,
      rescheduleParams,
    );

    window.open(scheduleUrl, '_self')?.focus();
  }

  getScheduledServices(getStorage = false): Observable<IResponseModel<ScheduledServicesModel[]>> {
    const {vin, country} = this.authService.urlInfo;

    // TODO: usar MAP ou storage? para armazenar os dados em cache
    const schedules = this.sessionService.getObject(StorageKeys.OPEN_SCHEDULES) as IResponseModel<ScheduledServicesModel[]>;

    if (schedules.response?.length && vin === schedules.response[0].vin && getStorage) {
      console.log('Schedules from cache');
      return new Observable(data => data.next(schedules));
    }

    const api = environment.getRoute(API.scheduling);
    const url = `${api}/schedules/v2/list`;

    return this.http.post<IResponseModel<ScheduledServicesModel[]>>(url, { vin, country, status: [1] }).pipe(
      map(data => {
        this.sessionService.setObject(StorageKeys.OPEN_SCHEDULES, data);
        return data;
      })
    );
  }

  getDriverInfo(country: string, dealerId: number, advisorId: number, scheduleDate: string, scheduleHour: number): Observable<any> {
    const api = environment.getRoute(API.transformation);
    const url = `${api}/schedules?advisor=${advisorId}&country=${country}&hour=${scheduleHour}&date=${scheduleDate}&dealer=${dealerId}`;
    const headers = {
      countryCode: country
    };
    return this.http.get<any>(url, { headers }).pipe(
      tap(data => data)
    );
  }

  getDriverSchedule(schedule: any): Observable<DriverInformationModel> {
    const api = environment.getRoute(API.scheduling);
    const url = `${api}/schedules/v2/drivers`;
    const scheduleDate = schedule.scheduleDate + 'T' + '00:00:00.000';

    const body = {
      vin: schedule.vin,
      advisor: schedule.advisorId,
      country: schedule.country,
      date: scheduleDate,
      dealer: schedule.dealerId,
      hour: schedule.scheduleHour,
    };
    const headers = {
      countryCode: schedule.country
    };

    return this.http.post<DriverInformationModel>(url, body, { headers }).pipe(
      tap(data => data)
    );
  }

  getImageDriverSchedule(fileName: string): Observable<string> {
    const api = environment.getRoute(API.dealermedia);
    const url = `${api}/images/get/advisor/v2`;

    return this.http.post(url, { fileName }, { responseType: 'arraybuffer' }).pipe(
      map((response: ArrayBuffer) => {
        let binary = '';
        const bytes = new Uint8Array(response);
        for (let i = 0; i < bytes.byteLength; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        return 'data:image/png;base64,' + btoa(binary);
      })
    );
  }

  cancelSchedule(schedule: ScheduledServicesModel, reschedule: boolean): Observable<IResponseModel<null>> {
    const { userGuid } = this.sessionService.getObject(StorageKeys.USER_PROFILE) as IUserProfileResponse;

    const openSchedules = this.sessionService.getObject(StorageKeys.OPEN_SCHEDULES) as IResponseModel<ScheduledServicesModel[]>;

    const api = environment.getRoute(API.transformation);
    const url = `${api}/schedules`;
    const cancelRequest = new CancelScheduleRequestModel(
      schedule.divisionId,
      schedule.country,
      schedule.dealerId,
      schedule.advisorId,
      schedule.scheduleHour,
      dayjs(schedule.scheduleDate).format('YYYY-MM-DD'),
      userGuid ?? '',
      reschedule
    );

    return this.http.delete<IResponseModel<null>>(url, { body: cancelRequest }).pipe(
      map(() => {
        this.sessionService.removeItem(StorageKeys.SCHEDULE_ID);
        const schedules = openSchedules.response.filter(s => s.scheduleId !== schedule.scheduleId);
        this.sessionService.setObject(StorageKeys.OPEN_SCHEDULES, { ...openSchedules, response: schedules });
        return { ...openSchedules, response: null };
      })
    );
  }

  getOfferedServicesByDealer(dealerId: string, country: string): Observable<IResponseModel<ScheduleServices[]>> {
    const api = environment.getRoute(API.dealer);
    const url = `${api}/dealers/${dealerId}/services?country=${country}`;

    return this.http.get<IResponseModel<ScheduleServices[]>>(url);
  }
}
